
    import Vue from "vue";
    import Component from "vue-class-component";
    import {Prop} from "vue-property-decorator";
    import TableEmptySlot from "@/features/shared/components/tables/TableEmptySlot.vue";
    import {ApplicationState, AreaOption, AreasClient, PracticeType, PracticeViewModel} from "@/api-client";
    import {formatDate} from "@/formatting/dateFormatter";
    import ClientsidePaging from "@/components/ClientsidePaging";
    import {BvTableFieldArray} from "bootstrap-vue";
    import translateApplicationState from "@/features/applications/components/ApplicationStateTranslator";
    import ValidityStatusBadge from "@/features/shared/components/ValidityStatusBadge.vue";
    import ClientsidePagingSelector from "@/components/ClientsidePagingSelector.vue";
    import translatePracticeKind from "@/features/shared/PracticeKindTranslator";

    @Component({
        components: {
            ValidityStatusBadge,
            TableEmptySlot,
            ClientsidePagingSelector
        }
    })
    export default class PracticeTable extends Vue {
        @Prop({ required: true})
        practices!: PracticeViewModel[];
        @Prop({ default: false})
        withPagination!: boolean;
        @Prop({ required: false})
        practiceType?: PracticeType;

        paging = new ClientsidePaging();
        areas: AreaOption[] = [];

        showSeafererShipDetails() {
            this.practices.forEach(item => {
                if (item.type == PracticeType.Seafaring && item.seafaringDetails != null) {
                    (item as PracticeViewModel & {_showDetails?: boolean})._showDetails = true
                }
            })
        }

        async created() {
            this.showSeafererShipDetails();
            await new AreasClient().areaOptions().then(areas => {
                this.areas = areas.areas;
            });
        }

        async beforeUpdate() {
            this.showSeafererShipDetails();
        }

        navigateToDetails(practice: PracticeViewModel) {
            switch (practice.type) {
                case PracticeType.ShipRepair:
                    this.$router.push({name: 'ship-repair-practice-details', params: {id: practice.id}});
                    return;

                case PracticeType.Seafaring:
                    this.$router.push({name: 'seafaring-practice-details', params: {id: practice.id}});
                    return;

                case PracticeType.InlandVessel:
                    this.$router.push({name: 'inland-vessel-practice-details', params: {id: practice.id}});
                    return;

                case PracticeType.Pilotage:
                    this.$router.push({name: 'pilotage-practice-details', params: {id: practice.id}});
                    return;

                case PracticeType.VtsCenter:
                    this.$router.push({name: 'vts-center-practice-details', params: {id: practice.id}});
                    return;

                default:
                    throw new Error("Unknown practice type '" + practice.type + "'!");
            }
        }

        fields() {
            let type = PracticeType.ShipRepair;
            // Use explicit practiceType prop if provided
            if (this.practiceType !== undefined) {
                type = this.practiceType;
            } else if(this.practices.length > 0) {
                type = this.practices[0].type;
            }
            switch (type) {
                case PracticeType.ShipRepair:
                    return [
                        {
                            key: 'position', label: this.$ls.Documents_Practices_Label_Position, sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => item.positionName ? item.positionName.toString() : '-'
                        },
                        { key: 'organizationName', label: this.$ls.PersonProfile_Practices_Table_Label_PracticeOrganization, sortable: true },
                        {
                            key: 'startedOn', label: this.$ls.PersonProfile_Practices_Table_Label_StartingDate, sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => formatDate(item.startedOn)
                        },
                        {
                            key: 'endedOn', label: this.$ls.PersonProfile_Practices_Table_Label_EndingDate, sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => formatDate(item.endedOn)
                        },
                        {
                            key: 'totalDays', label: this.$ls.Common_Label_Sum, sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => { let sum = 0; item.practicePeriods.forEach(f => sum += f.durationInDays??0); return sum.toString() }
                        },
                        {
                            key: 'status', label: this.$ls.PersonProfile_Practices_Table_Label_Status, sortable: true, sortByFormatted: true, thClass: 'w-10',
                            formatter: (state: ApplicationState): string => translateApplicationState(state)
                        },
                        { key: 'linkIcon', label: '', sortable: false, thClass: 'w-5', tdClass: 'table-actions' }
                    ] as BvTableFieldArray;

                case PracticeType.Seafaring:
                    return [
                        {
                            key: 'position', label: this.$ls.Documents_Practices_Label_Position, sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => item.positionName ? item.positionName.toString() : '-'
                        },
                        { key: 'kind', label: this.$ls.Applications_Requirements_Practices_Table_Label_PracticeKind, sortable: true, thClass:"w-8", formatter:(value: unknown, key:number, item: PracticeViewModel): string => translatePracticeKind(item.kind) },
                        { key: "shipName", label: this.$ls.Documents_Practices_Heading_Ship, formatter: (value: unknown, key: number, item: PracticeViewModel): string|undefined => item.seafaringDetails?.shipName },
                        { key: "imo", label: this.$ls.Documents_Practices_Label_ShipImoNumber_Short, formatter: (value: unknown, key: number, item: PracticeViewModel): string|null|undefined => item.seafaringDetails?.imoNr },
                        { key: "gt", label: this.$ls.Documents_Practices_Label_ShipGrossTonnage_Short, formatter: (value: unknown, key: number, item: PracticeViewModel): number|undefined => item.seafaringDetails?.shipGrossTonnage },
                        { key: "kw", label:  this.$ls.Documents_Practices_Label_ShipMainPower_Short, formatter: (value: unknown, key: number, item: PracticeViewModel): number|undefined => item.seafaringDetails?.shipMainPower },
                        {
                            key: 'startedOn', label: this.$ls.PersonProfile_Practices_Table_Label_StartingDate, sortable: true, thClass: "w-8",
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => formatDate(item.startedOn)
                        },
                        {
                            key: 'endedOn', label: this.$ls.PersonProfile_Practices_Table_Label_EndingDate, sortable: true, thClass: "w-8",
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => formatDate(item.endedOn)
                        },
                        {
                            key: 'totalDays', label: this.$ls.Common_Label_Sum, sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => { let sum = 0; item.practicePeriods.forEach(f => sum += f.durationInDays??0); return sum.toString() }
                        },
                        {
                            key: 'engine', label: this.$ls.Documents_Practices_Modal_AddPracticePeriod_Label_EngineRoomWatchDays_Short, sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => { let sum = 0; item.practicePeriods.forEach(f => sum += f.engineRoomWatchDays??0); return sum.toString() }
                        },
                        {
                            key: 'navigation', label: this.$ls.Documents_Practices_Modal_AddPracticePeriod_Label_NavigationalWatchDays_Short, sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => { let sum = 0; item.practicePeriods.forEach(f => sum += f.navigationalWatchDays??0); return sum.toString() }
                        },
                        {
                            key: 'status', label: this.$ls.PersonProfile_Practices_Table_Label_Status, sortable: true, sortByFormatted: true, tdClass: "table-status",
                            formatter: (state: ApplicationState): string => translateApplicationState(state)
                        },
                        { key: 'linkIcon', label: '', sortable: false, tdClass: 'table-actions' }
                    ] as BvTableFieldArray;

                case PracticeType.Pilotage:
                    return [
                        {
                            key: 'harbourName', label: this.$ls.Documents_Practices_Label_Harbour, sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => item.harbourName || '-'
                        },
                        {
                            key: 'pilotageArea', label: this.$ls.Documents_Practices_Label_PilotageArea, sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => item.pilotageArea || '-'
                        },
                        {
                            key: 'shipName', label: this.$ls.Documents_Practices_Heading_Ship, sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => item.pilotageShip?.name || '-'
                        },
                        {
                            key: 'imo', label: this.$ls.Documents_Practices_Label_ShipImoNumber_Short, sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => item.pilotageShip?.imoNr || '-'
                        },
                        {
                            key: 'gt', label: this.$ls.Documents_Practices_Label_ShipGrossTonnage_Short, sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => item.pilotageShip?.grossTonnage?.toString() || '-'
                        },
                        {
                            key: 'shipLength', label: this.$ls.Documents_Practices_Label_ShipLength, sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => item.pilotageShip?.lengthOverall ? `${item.pilotageShip.lengthOverall}m` : '-'
                        },
                        {
                            key: 'startedOn', label: this.$ls.PersonProfile_Practices_Table_Label_StartingDate, sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => formatDate(item.startedOn)
                        },
                        {
                            key: 'endedOn', label: this.$ls.PersonProfile_Practices_Table_Label_EndingDate, sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => formatDate(item.endedOn)
                        },
                        {
                            key: 'inOut', label: this.$ls.Documents_Practices_Modal_AddPracticePeriod_Label_InOutFlag, sortable: false,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => {
                                // Check all practice periods for unique in/out flags
                                const hasIn = item.practicePeriods.some(p => p.inOutFlag === true);
                                const hasOut = item.practicePeriods.some(p => p.inOutFlag === false);

                                if (hasIn && hasOut) {
                                    // Both In and Out exist - show both
                                    return `${this.$ls.Documents_Practices_InOutFlag_In} ${this.$ls.Documents_Practices_InOutFlag_Out}`;
                                } else if (hasIn) {
                                    // Only In exists
                                    return this.$ls.Documents_Practices_InOutFlag_In;
                                } else if (hasOut) {
                                    // Only Out exists
                                    return this.$ls.Documents_Practices_InOutFlag_Out;
                                }
                                return '-';
                            }
                        },
                        {
                            key: 'instructor', label: this.$ls.Documents_Practices_Modal_AddPracticePeriod_Label_Instructor, sortable: false,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => {
                                // Collect all unique instructor names from all practice periods
                                const instructorNames = new Set<string>();
                                item.practicePeriods.forEach(p => {
                                    if (p.instructorFullName) {
                                        instructorNames.add(p.instructorFullName);
                                    }
                                });

                                if (instructorNames.size > 0) {
                                    return Array.from(instructorNames).join(', ');
                                }
                                return '-';
                            }
                        },
                        {
                            key: 'instructorCode', label: this.$ls.Global_Label_PersonalCode, sortable: false,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => {
                                // Collect all unique instructor personal codes from all practice periods
                                const instructorCodes = new Set<string>();
                                item.practicePeriods.forEach(p => {
                                    if (p.instructorPersonalCode) {
                                        instructorCodes.add(p.instructorPersonalCode);
                                    }
                                });

                                if (instructorCodes.size > 0) {
                                    return Array.from(instructorCodes).join(', ');
                                }
                                return '-';
                            }
                        },
                        {
                            key: 'status', label: this.$ls.PersonProfile_Practices_Table_Label_Status, sortable: true, sortByFormatted: true,
                            formatter: (state: ApplicationState): string => translateApplicationState(state)
                        },
                        { key: 'linkIcon', label: '', sortable: false, thClass: 'w-5', tdClass: 'table-actions' }
                    ] as BvTableFieldArray;

                case PracticeType.InlandVessel:
                    return [
                        {
                            key: 'position', label: 'Amet', sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => item.positionName ? item.positionName.toString() : '-'
                        },
                        {
                            key: 'shipName', label: 'Laeva nimi', sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => item.inlandVesselShip?.name || '-'
                        },
                        {
                            key: 'startedOn', label: 'Algus', sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => formatDate(item.startedOn)
                        },
                        {
                            key: 'endedOn', label: 'Lõpp', sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => formatDate(item.endedOn)
                        },
                        {
                            key: 'totalDays', label: 'Kokku', sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => { let sum = 0; item.practicePeriods.forEach(f => sum += f.durationInDays??0); return sum.toString() }
                        },
                        {
                            key: 'harbourVisitsCount', label: 'Reiside arv', sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => {
                                // Sum up voyageCount from practice periods for Inland Vessel practices
                                let sum = 0;
                                item.practicePeriods.forEach(period => {
                                    if ((period as any).voyageCount) {
                                        sum += (period as any).voyageCount;
                                    }
                                });
                                // If we have voyage counts in periods, use that
                                if (sum > 0) {
                                    return sum.toString();
                                }
                                // Otherwise, fall back to harbourVisitsCount from the API (legacy data)
                                return item.harbourVisitsCount?.toString() || '0';
                            }
                        },
                        {
                            key: 'status', label: 'Olek', sortable: true, sortByFormatted: true,
                            formatter: (state: ApplicationState): string => translateApplicationState(state)
                        },
                        { key: 'linkIcon', label: '', sortable: false, thClass: 'w-5', tdClass: 'table-actions' }
                    ] as BvTableFieldArray;

                case PracticeType.VtsCenter:
                    return [
                        {
                            key: 'position', label: 'Amet', sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => item.positionName ? item.positionName.toString() : '-'
                        },
                        {
                            key: 'startedOn', label: 'Algus', sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => formatDate(item.startedOn)
                        },
                        {
                            key: 'endedOn', label: 'Lõpp', sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => formatDate(item.endedOn)
                        },
                        {
                            key: 'totalDays', label: 'Kokku', sortable: true,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => { let sum = 0; item.practicePeriods.forEach(f => sum += f.durationInDays??0); return sum.toString() }
                        },
                        {
                            key: 'notes', label: 'Märkused', sortable: false,
                            formatter: (value: unknown, key: string, item: PracticeViewModel): string => item.vtsCenterNotes || '-'
                        },
                        {
                            key: 'status', label: 'Olek', sortable: true, sortByFormatted: true,
                            formatter: (state: ApplicationState): string => translateApplicationState(state)
                        },
                        { key: 'linkIcon', label: '', sortable: false, thClass: 'w-5', tdClass: 'table-actions' }
                    ] as BvTableFieldArray;
            }
        }
    }
